import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { showApiError } from '@/lib/utils/notifications'
import { fetchMatchingRunDetail } from '../services/yardiMatchingService'
import { MatchStatusBadge } from '../components/MatchStatusBadge'
import type { MatchingAttempt, MatchingRun } from '../types'

export function YardiMatchingRunPage() {
  const { runId = '' } = useParams()
  const [run, setRun] = useState<MatchingRun | null>(null)
  const [attempts, setAttempts] = useState<MatchingAttempt[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!runId) return
    setLoading(true)
    fetchMatchingRunDetail(runId)
      .then((result) => {
        setRun(result.run)
        setAttempts(result.attempts)
      })
      .catch(showApiError)
      .finally(() => setLoading(false))
  }, [runId])

  const cards = [
    ['Processed', run?.residents_processed ?? 0],
    ['Would auto-map', run?.residents_would_auto_map ?? 0],
    ['Auto mapped', run?.residents_auto_mapped ?? 0],
    ['Manual review', run?.residents_manual_review ?? 0],
    ['Failed', run?.residents_failed ?? 0],
    ['Skipped', run?.residents_skipped ?? 0],
  ] as const

  return (
    <div className="min-h-full" data-testid="yardi-matching-run-page">
      <PageHeader title="Matching Run Details" testIdRoot="yardi-matching-run-header" />
      <div className="space-y-5 p-4 sm:p-6">
        <Link to="/yardi-matching" className="text-sm font-semibold text-[rgb(21,151,255)]">
          Back to configuration
        </Link>

        {loading ? (
          <div className="py-10 text-center text-slate-500">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
            Loading run…
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-semibold">Run #{run?.run_id}</h2>
                <MatchStatusBadge value={run?.status} />
                <MatchStatusBadge value={run?.mode} />
              </div>
              <p className="mt-2 text-sm text-slate-500">
                Started {run?.started_at || '--'} · Completed {run?.completed_at || 'in progress'}
              </p>
              {run?.error_summary ? <p className="mt-2 text-sm text-rose-600">{run.error_summary}</p> : null}
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
              {cards.map(([label, value]) => (
                <div key={label} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="text-xl font-semibold text-slate-900">{value}</div>
                </div>
              ))}
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Property</th>
                      <th className="px-4 py-3">Resident</th>
                      <th className="px-4 py-3">TCode</th>
                      <th className="px-4 py-3">Score</th>
                      <th className="px-4 py-3">Decision</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                          No attempt rows for this run.
                        </td>
                      </tr>
                    ) : (
                      attempts.map((attempt) => (
                        <tr key={attempt.attempt_id} className="border-t border-slate-100">
                          <td className="px-4 py-3">{attempt.property_name || attempt.liberty_property_id}</td>
                          <td className="px-4 py-3">{attempt.liberty_entity_id}</td>
                          <td className="px-4 py-3">{attempt.selected_tcode || '--'}</td>
                          <td className="px-4 py-3">{attempt.score ?? '--'}</td>
                          <td className="px-4 py-3">
                            <MatchStatusBadge value={attempt.decision} />
                          </td>
                          <td className="px-4 py-3">
                            <MatchStatusBadge value={attempt.status} />
                          </td>
                          <td className="px-4 py-3 text-slate-600">{attempt.reason || attempt.error_details || '--'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
